/**
 * Hell, dunkel oder wie das Betriebssystem.
 *
 * Bewusst selbst gebaut statt `next-themes`: Das Paket löst zusätzlich das
 * Flackern beim serverseitigen Rendern, und genau das gibt es hier nicht. Was
 * bleibt, sind dreißig Zeilen.
 *
 * Die Wahl liegt im `localStorage` und damit nur im jeweiligen Browser. Das ist
 * richtig so — welches Thema jemand am Handy im Studio will, hat mit dem
 * Schreibtisch nichts zu tun.
 *
 * **Der aufgelöste Zustand gehört mit in den Kontext.** Die Klasse am
 * `<html>`-Element ist eine Ausgabe, keine Quelle: Wer sie zum Rendern
 * ausliest, liest den Stand von vor dem letzten Effekt, und React erfährt von
 * ihrer Änderung ohnehin nichts. Genau daran hing der Schalter fest — nach dem
 * Einschalten des dunklen Themas zeigte er weiter auf „dunkel“, und ein
 * weiterer Druck setzte denselben Wert noch einmal, also passierte nichts
 * mehr.
 */

import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useState,
} from "react";

export type Thema = "light" | "dark" | "system";

const SCHLUESSEL = "gymbook.theme";

interface ThemaKontext {
  /** Die Wahl: hell, dunkel oder „wie das System“. */
  theme: Thema;
  /** Was daraus gerade tatsächlich zu sehen ist. */
  istDunkel: boolean;
  setTheme: (thema: Thema) => void;
}

const Kontext = createContext<ThemaKontext>({
  theme: "system",
  istDunkel: false,
  setTheme: () => {},
});

/** Die Wahl und das, was das System sagt, zu einem Zustand verrechnen. */
export function loeseAuf(thema: Thema, systemIstDunkel: boolean): boolean {
  return thema === "dark" || (thema === "system" && systemIstDunkel);
}

/** Der Umschalter: Gezeigt wird immer das Gegenteil des Sichtbaren. */
export function gegenteil(istDunkel: boolean): Thema {
  return istDunkel ? "light" : "dark";
}

function gespeichertesThema(): Thema {
  try {
    const wert = localStorage.getItem(SCHLUESSEL);
    if (wert === "light" || wert === "dark" || wert === "system") return wert;
  } catch {
    // Privater Modus oder blockierte Website-Daten: kein Grund, nicht zu starten.
  }
  return "system";
}

function systemIstDunkel(): boolean {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    // Ältere Testumgebungen kennen matchMedia nicht. Hell ist die sichere Wahl.
    return false;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemaZustand] = useState<Thema>(gespeichertesThema);
  // Gleich richtig statt erst nach dem ersten Effekt: Sonst zeigte der
  // Schalter für einen Bildaufbau lang das falsche Sinnbild.
  const [istDunkel, setIstDunkel] = useState(() =>
    loeseAuf(gespeichertesThema(), systemIstDunkel()),
  );

  useEffect(() => {
    const wurzel = document.documentElement;
    const abfrage = window.matchMedia("(prefers-color-scheme: dark)");

    const anwenden = () => {
      const dunkel = loeseAuf(theme, abfrage.matches);
      wurzel.classList.toggle("dark", dunkel);
      setIstDunkel(dunkel);
    };

    anwenden();
    // Nur bei "system" muss auf Änderungen gehört werden — sonst gilt die Wahl.
    if (theme !== "system") return;
    abfrage.addEventListener("change", anwenden);
    return () => abfrage.removeEventListener("change", anwenden);
  }, [theme]);

  const setTheme = useCallback((neu: Thema) => {
    setThemaZustand(neu);
    try {
      localStorage.setItem(SCHLUESSEL, neu);
    } catch {
      // Nicht speichern zu können heißt nur: gilt für diese Sitzung.
    }
  }, []);

  return <Kontext value={{ theme, istDunkel, setTheme }}>{children}</Kontext>;
}

export function useTheme(): ThemaKontext {
  return use(Kontext);
}
